import { useState, useEffect, Fragment } from 'react';
import { getUserData } from '../../utils/auth';
import { apiRequest, trickleCreateObject, trickleDeleteObject, trickleListObjects, trickleUpdateObject } from '../../utils/api';
import RssFeedPreview from './RssFeedPreview';
import RssEmptyState from './RssEmptyState';
import RssSubscriptionCard from './RssSubscriptionCard';
import Toast from '../Toast';
import { scrapeWebsiteContent } from '../../utils/websiteScraper';
import { dispatchCreditUpdateEvent } from '../../utils/creditSystem';

function RssSubscriptionTool() {
    const [url, setUrl] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [feedData, setFeedData] = useState(null);
    const [subscriptions, setSubscriptions] = useState([]);
    const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(true);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [showSubscriptionForm, setShowSubscriptionForm] = useState(false);
    const [selectedFeed, setSelectedFeed] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [isScrapering, setIsScrapering] = useState(false);
    try {
    
        const handleScraper = async (e, sub_id, item) => {
            e.preventDefault();
            e.stopPropagation();

            setIsScrapering(true);
            try {
                // Then scrape the website
                const result = await apiRequest('POST', '/api/index/rssToText', {
                    id: item.id
                });
                
                if (!result.success) {
                    throw new Error(result.error || '擷取內容失敗，請稍後再試');
                }

                setToast({
                    show: true,
                    message: '處理成功，請前往“轉換記錄”進一步處理。',
                    type: 'success'
                });
                
                dispatchCreditUpdateEvent(result.data.credit);
                setSubscriptions(prev => {
                    return prev.map((sub) => {
                        if (sub_id == sub.id) {
                            sub.items = sub.items.map((_item) => {
                                if (_item.id == item.id) {
                                    _item.confirm = false;
                                    _item.is_process = 1;
                                }
                                return _item;
                            })
                        }
                        return sub;
                    })
                })
            } catch (err) {
                setError(err.message || '擷取內容失敗，請稍後再試');
                console.error('Website scraping error:', err);
            } finally {
                setIsScrapering(false);
            }
        }

        useEffect(() => {
            fetchSubscriptions();
        }, [refreshKey]);

        const fetchSubscriptions = async () => {
            setIsSubscriptionLoading(true);
            try {
                const userData = getUserData();
                if (!userData || !userData.id) {
                    throw new Error('用戶未登入');
                }

                const result = await apiRequest('POST', '/api/index/get_rss');
                
                if (result.success && result.data.length > 0) {
                    const sortedSubscriptions = result.data
                    
                    setSubscriptions(sortedSubscriptions);
                } else {
                    setSubscriptions([]);
                }
            } catch (error) {
                console.error('Failed to fetch subscriptions:', error);
                setError('無法載入訂閱資訊');
            } finally {
                setIsSubscriptionLoading(false);
            }
        };

        const handleShowConfirm = (sub_id, item_id, is_show) => {
            setSubscriptions(prev => {
                return prev.map((item) => {
                    if (sub_id == item.id) {
                        item.items = item.items.map((_item) => {
                            if (_item.id == item_id) {
                                _item.confirm = is_show;
                            } else {
                                _item.confirm = false;
                            }
                            return _item;
                        })
                    }
                    return item;
                })
            })
        }

        const handleSubmit = async (e) => {
            e.preventDefault();
            setError('');
            setFeedData(null);

            if (!url.trim()) {
                setError('請輸入 RSS 網址');
                return;
            }

            if (!isValidUrl(url)) {
                setError('請輸入有效的網址');
                return;
            }

            setIsLoading(true);

            try {
                const result = await apiRequest('POST', '/api/index/rss', { url });
                if (!result.success) {
                    throw new Error(result.error || '無法擷取 RSS 內容');
                }
                setFeedData(result.data);

                setShowSubscriptionForm(true);
            } catch (error) {
                console.error('Failed to fetch RSS feed:', error);
                setError(error.message || '無法擷取 RSS 內容，請確認網址是否正確');
            } finally {
                setIsLoading(false);
            }
        };

        const handleSubscribe = async () => {
            if (!feedData) {
                setError('無法訂閱，請重新擷取 RSS 內容');
                return;
            }

            setIsLoading(true);
            try {
                const userData = getUserData();
                if (!userData || !userData.id) {
                    throw new Error('用戶未登入');
                }
                
                const res = await apiRequest('POST', '/api/index/rss_subscribe', {
                    url
                });
                if (!res.success) {
                    throw new Error(res.error || '無法訂閱，請重新擷取 RSS 內容');
                }
                
                setToast({
                    show: true,
                    message: '成功訂閱 RSS 源',
                    type: 'success'
                });
                
                setUrl('');
                setFeedData(null);
                setShowSubscriptionForm(false);
                setRefreshKey(prev => prev + 1); // Trigger refresh
            } catch (error) {
                console.error('Failed to subscribe to RSS feed:', error);
                setError(error.message || '訂閱失敗，請稍後再試');
            } finally {
                setIsLoading(false);
            }
        };

        const handleDelete = async (subscription) => {
            setIsDeleting(true);
            try {
                const userData = getUserData();
                if (!userData || !userData.id) {
                    throw new Error('用戶未登入');
                }

                const result = await apiRequest('POST', '/api/index/rssUnsubscribe', {
                    id: subscription.id
                })
                if (!result.success) {
                    throw new Error(result.error);
                }
                
                setToast({
                    show: true,
                    message: '已取消訂閱',
                    type: 'success'
                });
                
                setRefreshKey(prev => prev + 1); // Trigger refresh
            } catch (error) {
                console.error('Failed to delete subscription:', error);
                setToast({
                    show: true,
                    message: '取消訂閱失敗，請稍後再試',
                    type: 'error'
                });
            } finally {
                setIsDeleting(false);
            }
        };

        const handleRefreshFeed = async (subscription) => {
            setSelectedFeed(subscription);
            setIsLoading(true);
            try {
                // First use a credit
                // await useCredit('rss_refresh', { url: subscription.feedUrl });
                
                // Then fetch RSS feed
                const result = await apiRequest('POST', '/api/index/rssRefresh', {
                    id: subscription.id
                })
                
                if (!result.success) {
                    throw new Error(result.error || '無法更新 RSS 內容');
                }
                
                setToast({
                    show: true,
                    message: '已更新 RSS 源內容',
                    type: 'success'
                });
                
                setRefreshKey(prev => prev + 1); // Trigger refresh
            } catch (error) {
                console.error('Failed to refresh RSS feed:', error);
                setToast({
                    show: true,
                    message: '更新失敗，請稍後再試',
                    type: 'error'
                });
            } finally {
                setIsLoading(false);
                setSelectedFeed(null);
            }
        };

        const isValidUrl = (string) => {
            try {
                new URL(string);
                return true;
            } catch (_) {
                return false;
            }
        };

        return (
            <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden" data-name="rss-subscription-container">
                    <div className="px-8 py-6 bg-gradient-to-r from-orange-600 to-orange-400">
                        <h1 className="text-2xl font-bold text-white" data-name="page-title">
                            RSS 訂閱管理
                        </h1>
                        <p className="text-white/80 mt-2">
                            訂閱您喜愛的網站 RSS 源，隨時獲取最新內容
                        </p>
                    </div>

                    <div className="p-8">
                        <form onSubmit={handleSubmit} className="space-y-6" data-name="rss-form">
                            <div>
                                <label htmlFor="rss-url" className="block text-sm font-medium text-gray-700 mb-2">
                                    RSS 訂閱網址
                                </label>
                                <div className="flex gap-4">
                                    <div className="relative flex-1">
                                        <input
                                            type="url"
                                            id="rss-url"
                                            value={url}
                                            onChange={(e) => setUrl(e.target.value)}
                                            placeholder="https://example.com/feed.xml"
                                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-200 focus:border-orange-400 transition-all pl-10"
                                            disabled={isLoading}
                                            data-name="url-input"
                                        />
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <i className="fas fa-rss text-orange-400"></i>
                                        </div>
                                    </div>
                                    <button
                                        type="submit"
                                        className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 focus:ring-4 focus:ring-orange-200 transition-all flex items-center justify-center min-w-[120px]"
                                        disabled={isLoading}
                                        data-name="submit-button"
                                    >
                                        {isLoading ? (
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                        ) : (
                                            <Fragment>
                                                <i className="fas fa-sync mr-2"></i>
                                                擷取內容
                                            </Fragment>
                                        )}
                                    </button>
                                </div>
                                {error && (
                                    <p className="mt-2 text-sm text-red-600" data-name="url-error">
                                        <i className="fas fa-exclamation-circle mr-1"></i>
                                        {error}
                                    </p>
                                )}
                            </div>

                            {feedData && showSubscriptionForm && (
                                <RssFeedPreview 
                                    feed={feedData} 
                                    onSubscribe={handleSubscribe} 
                                    onCancel={() => setShowSubscriptionForm(false)}
                                    isLoading={isLoading}
                                />
                            )}
                        </form>

                        <div className="mt-12">
                            <h2 className="text-xl font-bold mb-6">我的訂閱</h2>
                            
                            {isSubscriptionLoading ? (
                                <div className="flex justify-center py-12">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                                </div>
                            ) : subscriptions.length === 0 ? (
                                <RssEmptyState />
                            ) : (
                                <div className="space-y-6">
                                    {subscriptions.map(subscription => (
                                        <RssSubscriptionCard
                                            key={subscription.id}
                                            subscription={subscription}
                                            onDelete={handleDelete}
                                            onRefresh={handleRefreshFeed}
                                            isRefreshing={isLoading && selectedFeed && selectedFeed.id === subscription.id}
                                            isDeleting={isDeleting}
                                            onScraper={handleScraper}
                                            onShowConfirm={handleShowConfirm}
                                            isScrapering={isScrapering}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="mt-8 border-t pt-8" data-name="instructions">
                            <h2 className="text-xl font-semibold mb-4">使用說明</h2>
                            <div className="grid md:grid-cols-3 gap-6">
                                <div className="flex items-start space-x-3">
                                    <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                                        <i className="fas fa-rss text-orange-600"></i>
                                    </div>
                                    <div>
                                        <h3 className="font-medium mb-1">輸入 RSS 網址</h3>
                                        <p className="text-sm text-gray-600">輸入網站的 RSS 訂閱網址</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                                        <i className="fas fa-plus text-orange-600"></i>
                                    </div>
                                    <div>
                                        <h3 className="font-medium mb-1">訂閱 RSS 源</h3>
                                        <p className="text-sm text-gray-600">確認內容後點擊訂閱按鈕</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                                        <i className="fas fa-bell text-orange-600"></i>
                                    </div>
                                    <div>
                                        <h3 className="font-medium mb-1">接收更新</h3>
                                        <p className="text-sm text-gray-600">隨時查看您訂閱的最新內容</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {toast.show && (
                    <Toast 
                        message={toast.message} 
                        type={toast.type} 
                        onClose={() => setToast({ ...toast, show: false })} 
                    />
                )}
            </div>
        );
    } catch (error) {
        console.error('RssSubscriptionTool component error:', error);
        reportError(error);
        return null;
    }
}

export default RssSubscriptionTool;